import React from 'react';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import type { Employee } from '../../../store/slices/employeeSlice';
import { PersonCell, TableEmptyRow } from '../../../components/ui';
import { sbg } from '../../../theme/sbgTokens';
import { formatNumber, MISSING } from '../../../utils/format';
import { jobTitleOf, SelectVia } from './orgChartModel';
import { DeptDot, HighlightText, revealRowActionsSx, RowActions } from './OrgChartBits';

export interface OrgListTableProps {
  rows: Employee[];
  searchTerm: string;
  focusedId: string | null;
  reportsByManager: Map<string, Employee[]>;
  managerNameOf: (employee: Employee) => string | null;
  departmentOf: (employee: Employee) => { label: string; color: string };
  filterActive: boolean;
  onClearFilters: () => void;
  canEdit: boolean;
  canViewProfile: boolean;
  onSelect: (id: string, via?: SelectVia) => void;
  onEdit: (employee: Employee) => void;
  onViewProfile: (id: string) => void;
}

/** Everyone in the org as a table, A–Z. Selecting a row opens their details. */
const OrgListTable: React.FC<OrgListTableProps> = ({
  rows,
  searchTerm,
  focusedId,
  reportsByManager,
  managerNameOf,
  departmentOf,
  filterActive,
  onClearFilters,
  canEdit,
  canViewProfile,
  onSelect,
  onEdit,
  onViewProfile,
}) => {
  const hasActions = canEdit || canViewProfile;
  const columnCount = hasActions ? 6 : 5;

  return (
    <Table stickyHeader size="small" aria-label="People in the org" sx={{ minWidth: 760 }}>
      <TableHead>
        <TableRow>
          <TableCell>Person</TableCell>
          <TableCell>Title</TableCell>
          <TableCell>Department</TableCell>
          <TableCell>Reports to</TableCell>
          <TableCell align="right">Direct reports</TableCell>
          {hasActions && (
            <TableCell align="right" sx={{ width: 96 }}>
              <span className="sbg-sr-only">Actions</span>
            </TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableEmptyRow
            colSpan={columnCount}
            title={filterActive ? 'No people match these filters' : 'No people yet'}
            body={filterActive ? 'Try another name, or clear the search and department filter.' : 'People appear here once they’re added as active employees.'}
            action={
              filterActive ? (
                <Button variant="outlined" onClick={onClearFilters}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          rows.map((employee) => {
            const department = departmentOf(employee);
            const reportsCount = (reportsByManager.get(employee._id) || []).length;
            const selected = focusedId === employee._id;
            const jobTitle = jobTitleOf(employee);
            return (
              <TableRow
                key={employee._id}
                hover
                selected={selected}
                tabIndex={0}
                aria-selected={selected}
                data-select-person={employee._id}
                data-selected={selected || undefined}
                onClick={() => onSelect(employee._id, 'pointer')}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(employee._id, 'keyboard');
                  }
                }}
                sx={{ cursor: 'pointer', ...revealRowActionsSx }}
              >
                <TableCell sx={{ maxWidth: 280 }}>
                  <PersonCell
                    name={<HighlightText text={employee.name} highlight={searchTerm} />}
                    avatarName={employee.name}
                    secondary={employee.email}
                  />
                </TableCell>
                <TableCell sx={{ color: jobTitle ? sbg.text.primary : sbg.text.secondary }}>
                  {jobTitle ?? MISSING}
                </TableCell>
                <TableCell>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
                    <DeptDot color={department.color} />
                    {department.label}
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{managerNameOf(employee) || MISSING}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {reportsCount > 0 ? formatNumber(reportsCount) : MISSING}
                </TableCell>
                {hasActions && (
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', py: 0 }}>
                    <RowActions
                      employee={employee}
                      canEdit={canEdit}
                      canViewProfile={canViewProfile}
                      onEdit={onEdit}
                      onViewProfile={onViewProfile}
                    />
                  </TableCell>
                )}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
};

export default OrgListTable;
